import { type FC, useCallback } from 'react';
import { UiButton } from '@/lib/ui';
import { useCartQuery } from '@/registration/hook/useCartQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { useNavigate } from 'react-router-dom';
import { useTenantRoute } from '@/account/hook/useTenantRoute';
import { generatePageUrl } from '@/app/pages';
import { useGroupCartQuery } from '@/registration/hook/useGroupCartQuery';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';

export interface RemindToAddTicketsProps {
  isGroup?: boolean
  isUpsell?: boolean
}

const RemindToAddTickets: FC<RemindToAddTicketsProps> = ({
  isGroup = false,
  isUpsell = false,
}) => {
  const { tenantCode, eventId } = useRegisterRoute();
  const selectedCartQuery = isGroup ? useGroupCartQuery : useCartQuery;
  const cartQuery = selectedCartQuery(eventId);
  const { data: registration } = useRegistrationQuery(eventId, { enabled: isUpsell });

  const navigate = useNavigate();
  const { tenant } = useTenantRoute();

  const goBack = useCallback(
    () => {
      if (tenant?.code && eventId) {
        if (isGroup) {
          navigate(generatePageUrl('RegistrationRegisterGroupMainTickets', { tenantCode: tenant.code, eventId, }));
        } else {
          navigate(generatePageUrl('RegistrationRegisterMainTickets', { tenantCode: tenant.code, eventId, }));
        }
      }
    },
    [tenant?.code, eventId, navigate]
  );

  const goBackToUpsell = useCallback(
    () => {
      if (tenant?.code && eventId && registration?.registrationUpsellSteps[0]?.routeName) {
        navigate(generatePageUrl(registration.registrationUpsellSteps[0]?.routeName, { tenantCode, eventId }));
      }
    },
    [tenant?.code, eventId, registration?.registrationUpsellSteps, navigate, tenantCode]
  );

  return (
    <QueryContainer query={cartQuery} errorMessage="Failed to load the shopping cart.">
      {(shoppingCart) => {
        return (
          <>
            {(!shoppingCart?.cartItems || shoppingCart.cartItems.length === 0) && !isUpsell && (
              <BaseMessageBarError py={2}>To continue with your registration, please <UiButton onClick={goBack} variant={'outline'} fontSize={'lg'} m={2}>Go back</UiButton> to add tickets to your cart first.</BaseMessageBarError>
            )}

            {(!shoppingCart?.cartItems || shoppingCart.cartItems.length === 0) && isUpsell && (
              <BaseMessageBarError py={2}>To continue with your registration, please <UiButton onClick={goBackToUpsell} variant={'outline'} fontSize={'lg'} m={2}>Go back</UiButton> to add tickets to your cart first.</BaseMessageBarError>
            )}
          </>
        );
      }}
    </QueryContainer>
  );
};

export default RemindToAddTickets;
